import React, { useCallback, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Spinner } from "@fluentui/react-components";
import { useNavigate } from "react-router";
import { telephonyService, type CallLogDto } from "../../services/telephonyService";
import { ContactSearchBox } from "./AssignNumberDialog";
import { kontakteDesAnrufs, kontaktName } from "./callKontakte";
import { PersonIcon } from "./telephonyUtils";

/**
 * Die Kontakte eines Anrufs im Anruf-Detail (PO 28.09.2026: „Das Telefonat
 * einem oder mehreren Kontakten zuweisen").
 *
 * Chips: Hauptkontakt zuerst und markiert; je Chip „Als Hauptkontakt" und ×.
 * „Kontakt hinzufügen" sucht mit derselben Suchbox wie der Nummer-Zuordnen-
 * Dialog. Anders als dort wird die Rufnummer NICHT an den Kontakt geschrieben
 * — ein weiterer Kontakt heißt „war mit dran", nicht „dem gehört die Nummer".
 *
 * Jede Änderung liefert den Anruf frisch vom Server zurück (`onChanged`),
 * damit Zeile und Detail dieselben Kontakte zeigen wie die Datenbank.
 */
const CallKontakteBlock: React.FC<{
    call: CallLogDto;
    onChanged: (next: CallLogDto) => void;
}> = ({ call, onChanged }) => {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);

    const kontakte = kontakteDesAnrufs(call);

    const run = useCallback(
        async (op: () => Promise<CallLogDto>) => {
            setBusy(true);
            setError(null);
            try {
                onChanged(await op());
                return true;
            } catch (e) {
                setError((e as Error).message);
                return false;
            } finally {
                setBusy(false);
            }
        },
        [onChanged],
    );

    const hinzufuegen = useCallback(
        async (contactId: number) => {
            if (kontakte.some((k) => k.id === contactId)) {
                setAdding(false);
                return;
            }
            if (await run(() => telephonyService.addCallContact(call.id, contactId))) setAdding(false);
        },
        [call.id, kontakte, run],
    );

    return (
        <div className="tp-block">
            <div className="tp-block-h">
                {PersonIcon}
                Kontakte
                {busy && <Spinner size="tiny" />}
                {error && (
                    <span className="tp-muted" style={{ color: "var(--danger)" }}>
                        {error}
                    </span>
                )}
            </div>
            <div className="tp-kchips">
                {kontakte.map((k) => (
                    <span key={k.id} className={`tp-kchip${k.isPrimary ? " primary" : ""}`}>
                        <button
                            type="button"
                            className="tp-kchip-name"
                            onClick={() => navigate(`/contacts/${k.id}`)}
                            title={`Kontakt öffnen: ${kontaktName(k)}`}
                        >
                            {kontaktName(k)}
                        </button>
                        {k.isPrimary ? (
                            <span className="tp-kchip-tag">Hauptkontakt</span>
                        ) : (
                            <button
                                type="button"
                                className="tp-kchip-act"
                                disabled={busy}
                                onClick={() =>
                                    void run(() =>
                                        telephonyService.addCallContact(call.id, k.id, { primary: true }),
                                    )
                                }
                                title="Zum Hauptkontakt machen — sein Name steht dann in der Anrufliste"
                            >
                                Als Hauptkontakt
                            </button>
                        )}
                        <button
                            type="button"
                            className="tp-kchip-act"
                            disabled={busy}
                            onClick={() => void run(() => telephonyService.removeCallContact(call.id, k.id))}
                            aria-label={`${kontaktName(k)} vom Anruf entfernen`}
                            title={
                                k.isPrimary && kontakte.length > 1
                                    ? "Entfernen — der nächste Kontakt wird Hauptkontakt"
                                    : "Vom Anruf entfernen"
                            }
                        >
                            ×
                        </button>
                    </span>
                ))}
                <button
                    type="button"
                    className="tp-detail-btn"
                    disabled={busy}
                    onClick={() => {
                        setError(null);
                        setAdding(true);
                    }}
                >
                    + Kontakt hinzufügen
                </button>
            </div>

            {adding &&
                createPortal(
                    <div className="kvp-rd kvp-rd-scrim" onClick={busy ? undefined : () => setAdding(false)}>
                        <div
                            className="dm"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Kontakt zum Anruf hinzufügen"
                            style={{ width: "min(520px, 100%)" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="dm-head">
                                <div className="dm-head-top">
                                    <div className="dm-titles">
                                        <div className="dm-kicker">Anruf</div>
                                        <h2>Kontakt hinzufügen</h2>
                                    </div>
                                    <button
                                        className="dm-x"
                                        type="button"
                                        onClick={() => setAdding(false)}
                                        aria-label="Schließen"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <div className="dm-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                <div style={{ font: "500 13px/1.45 var(--ui)", color: "var(--ink-2, #55606b)" }}>
                                    {kontakte.length === 0
                                        ? "Der erste Kontakt wird Hauptkontakt des Anrufs."
                                        : "Der Anruf erscheint dann auch in der Akte dieses Kontakts. Die Rufnummer bleibt beim bisherigen Kontakt."}
                                </div>
                                <ContactSearchBox
                                    placeholder="Privatkontakt oder Firma suchen …"
                                    onPick={(c: { id: number }) => void hinzufuegen(Number(c.id))}
                                    autoFocus
                                    disabled={busy}
                                />
                                {error && <div className="rcp-err">{error}</div>}
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default CallKontakteBlock;
